import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { banStatus } from "./banStatus";
import { BAN_STATUS_TTL, banStatusKey, type BanStatusScope, type BanStatusSnapshot } from "./banStatusCache";

export const useBanStatus = (scope: BanStatusScope | null, delay = 0) => {
	const key = scope ? banStatusKey(scope) : "";
	const cached = scope ? banStatus.peek(scope) : undefined;
	const [state, setState] = useState<{ key: string; busy: boolean; snapshot?: BanStatusSnapshot; error?: string }>({ key, busy: !!scope && !cached, snapshot: cached });
	useEffect(() => {
		if (!scope) return;
		let active = true, sequence = 0;
		let timer: ReturnType<typeof setTimeout>;
		const load = (refresh = false) => {
			if (!active) return;
			const version = ++sequence;
			clearTimeout(timer);
			const hit = !refresh ? banStatus.peek(scope) : undefined;
			setState({ key, busy: !hit, snapshot: hit });
			void banStatus.load(scope, refresh).then(snapshot => {
				if (!active || version !== sequence) return;
				setState({ key, busy: false, snapshot });
				timer = setTimeout(() => load(), Math.max(1, snapshot.checkedAt + BAN_STATUS_TTL - Date.now()));
			}, error => {
				if (active && version === sequence) setState({ key, busy: false, error: error instanceof Error ? error.message : "load" });
			});
		};
		const unsubscribe = banStatus.subscribe(scope, () => load());
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { active = false; clearTimeout(timer); setState({ key, busy: false }); });
		if (delay && !banStatus.peek(scope)) timer = setTimeout(() => load(), delay);
		else load();
		return () => { active = false; clearTimeout(timer); unsubscribe(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, [key, delay]);
	const current = state.key === key ? state : { key, busy: !!scope };
	return { ...current, refresh: () => { if (scope) banStatus.invalidate(scope); } };
};
